// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/**
 * The first review, explained (workshop Plan 1; redone 2026-09-27 after design
 * review; the export card redone October 4, 2026).
 *
 * Two small things. Above the documents list, one line says how many documents are
 * ready to go out, with Set up export or Export (exportCardState). The Fields heading
 * says how many things on the open document to check, or a line above the fields says
 * why it waits (ToCheckLine.tsx); that stays, because "why is this waiting?" is a
 * question every day.
 */

import { hostHas } from '../shared/host-capabilities';
import { useEffect, useRef, useState } from 'react';
import {
  Check,
  CheckCircle2,
  FolderOpen,
  FolderOutput,
  History,
  LoaderCircle,
  Power,
  Repeat,
  Settings2,
  X,
} from 'lucide-react';
import type { ExportRunResult, ProcessResult } from '../shared/desktop-api';
import { TECHNICAL_DETAILS_MARKER } from '../shared/error-details';
import {
  exportHasDestination,
  exportRunNotice,
  exportRunNoticeCode,
  exportSwitchRequest,
} from '../shared/export-presentation';
import { announceExportDestinationChanged } from './connection-use';
import type { PublishNotice } from './WorkspaceNotice';
import { exportCardState, type ReadingProgress, noteMilestone, setupNextCard } from './onboarding';
import { useExportReadyCount } from './export-ready-count';
import { OperatorErrorText } from './TechnicalDetails';
import { folderImportStatus, type FolderImportProgress } from './folder-import-progress';

type ReviewDocument = ProcessResult['documents'][number];

/** Asks the app to open Job setup at a section (the job door does the same). */
export const OPEN_SETUP_SECTION_EVENT = 'capturepoint-open-setup-section';
export const openSetupSection = (section: 'export', stop?: 'when' | 'destination' | 'activity') =>
  window.dispatchEvent(
    new CustomEvent(OPEN_SETUP_SECTION_EVENT, { detail: stop ? `${section}:${stop}` : section }),
  );

/** What the card needs from the job's export settings; null until read. */
type ExportSetup = { setUp: boolean; sending: boolean; manual: boolean; folder: boolean };
function useExportSetup(jobId: string): { setup: ExportSetup | null; reread: () => void } {
  const [setup, setSetup] = useState<ExportSetup | null>(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let live = true;
    const api = window.capturePointDesktop;
    if (!api) return;
    const read = () =>
      void api
        .getExportSettings(jobId)
        .then((settings) => {
          if (live)
            setSetup({
              setUp: Boolean(settings?.destinationRoot?.trim() || settings?.destinationBindingId),
              sending: settings?.enabled !== false,
              manual: settings?.triggerMode === 'manual',
              folder: (settings?.destinationKind ?? 'filesystem') === 'filesystem',
            });
        })
        .catch(() => {
          if (live) setSetup({ setUp: false, sending: true, manual: true, folder: true });
        });
    read();
    const timer = window.setInterval(read, 5000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [jobId, revision]);
  return { setup, reread: () => setRevision((value) => value + 1) };
}

const ready = (count: number) => `${count.toLocaleString('en-US')} ready to export`;

/**
 * Above the documents list: how many documents are ready to go out, in one line, with Set up
 * export or Export (onboarding.ts, exportCardState; owner, October 4, 2026), or, with export
 * turned off, "Export is off" with the documents waiting and Turn on (owner, 2026-10-31). No
 * destination, folder or file names. Exporting shows a moving bar (the engine reports no
 * progress, so none is claimed); afterwards "38 exported" with Open folder for a folder
 * destination and See activity for any other (Content Central), which opens Export at its
 * Activity.
 */
export function FirstExportCard({
  jobId,
  documents,
  onNotice,
  onRefresh,
  reading = null,
}: {
  jobId: string;
  documents: readonly ReviewDocument[];
  onNotice: PublishNotice;
  onRefresh: (batch: ProcessResult) => void;
  /** Set while this batch is still being read. */
  reading?: ReadingProgress;
}) {
  const { setup, reread } = useExportSetup(jobId);
  const counted = useExportReadyCount(
    jobId,
    Boolean(setup?.setUp && setup.sending && setup.manual),
  );
  const [busy, setBusy] = useState(false);
  /** How many documents the running export was asked to send. */
  const [sending, setSending] = useState(0);
  const [done, setDone] = useState<ExportRunResult | null>(null);
  const [problem, setProblem] = useState('');
  const [turning, setTurning] = useState(false);
  const running = useRef(false);
  // A document confirmed: the only confirm there is (single confirm, 2026-10-05).
  const anyConfirmation = documents.some((item) => item.confirmedAsWhole);
  useEffect(() => {
    if (anyConfirmation) noteMilestone('firstConfirmation');
  }, [anyConfirmation]);

  if (done) {
    // Only an exported document has a destination to open (uncertain and canceled ones have none).
    const first = done.documents.find((document) => document.state === 'exported');
    return (
      <section className="export-ready done" aria-label="Export" role="status">
        <span className="export-ready-mark" aria-hidden="true">
          <CheckCircle2 size={16} />
        </span>
        <strong>
          {done.exported === 1 ? '1 exported' : `${done.exported.toLocaleString('en-US')} exported`}
        </strong>
        {setup?.folder !== false && first ? (
          <button
            type="button"
            className="secondary-button small"
            onClick={() =>
              void window.capturePointDesktop
                ?.openExportedDocument(first.exportId, 'root')
                .catch(() =>
                  onNotice('The export folder could not be opened.', 'export-folder-open-failed'),
                )
            }
          >
            <FolderOpen size={14} aria-hidden="true" />
            Open folder
          </button>
        ) : (
          <button
            type="button"
            className="secondary-button small"
            onClick={() => openSetupSection('export', 'activity')}
          >
            <History size={14} aria-hidden="true" />
            See activity
          </button>
        )}
        <button
          type="button"
          className="icon-button export-ready-close"
          aria-label="Close"
          onClick={() => setDone(null)}
        >
          <X size={15} />
        </button>
      </section>
    );
  }

  if (busy)
    return (
      <section
        className="export-ready exporting"
        aria-label="Export"
        role="status"
        aria-live="polite"
      >
        <span className="export-ready-mark" aria-hidden="true">
          <LoaderCircle className="spin" size={16} />
        </span>
        <strong>Exporting {sending.toLocaleString('en-US')}…</strong>
        <span className="export-ready-bar" aria-hidden="true">
          <i />
        </span>
      </section>
    );

  if (!setup) return null;
  const state = exportCardState({ documents, ...setup, readyToSend: counted.ready, reading });
  if (!state) return null;

  if (state.kind === 'set-up-export')
    return (
      <section className="export-ready" aria-label="Export">
        <span className="export-ready-mark" aria-hidden="true">
          <FolderOutput size={16} />
        </span>
        <strong>{ready(state.ready)}</strong>
        <button
          type="button"
          className="secondary-button small"
          data-telemetry="first-review-set-up-export"
          onClick={() => openSetupSection('export', 'destination')}
        >
          <Settings2 size={14} aria-hidden="true" />
          Set up export
        </button>
      </section>
    );

  if (state.kind === 'off') {
    const turnOn = async () => {
      if (turning) return;
      setTurning(true);
      setProblem('');
      try {
        const api = window.capturePointDesktop;
        if (!api) throw new Error('Export is not available in this build.');
        // The switch's own save, as in Export: the saved settings exactly, turned on.
        const saved = await api.getExportSettings(jobId);
        if (!saved || !exportHasDestination(saved)) {
          openSetupSection('export', 'destination');
          return;
        }
        try {
          await api.saveExportSettings(exportSwitchRequest(saved, true));
        } catch (reason) {
          throw new Error(
            `Export wasn’t turned on.${TECHNICAL_DETAILS_MARKER}${reason instanceof Error ? reason.message : String(reason)}`,
            { cause: reason },
          );
        }
        announceExportDestinationChanged();
        reread();
      } catch (reason) {
        setProblem(
          reason instanceof Error ? reason.message : 'Export wasn’t turned on. Try again.',
        );
      } finally {
        setTurning(false);
      }
    };
    return (
      <section className="export-ready off" aria-label="Export">
        <span className="export-ready-mark" aria-hidden="true">
          <Power size={16} />
        </span>
        <strong>
          Export is off{state.ready > 0 ? ` · ${state.ready.toLocaleString('en-US')} waiting` : ''}
        </strong>
        <button
          type="button"
          className="primary-button small"
          data-telemetry="first-review-turn-on-export"
          disabled={turning}
          onClick={() => void turnOn()}
        >
          <Power size={14} aria-hidden="true" />
          {turning ? 'Turning on…' : 'Turn on'}
        </button>
        {/* A <div>: the message can carry a Technical details disclosure, which a <p> may not hold. */}
        {problem && (
          <div
            className="export-ready-problem"
            role="alert"
            data-telemetry="error:export-not-turned-on"
          >
            <OperatorErrorText message={problem} />
          </div>
        )}
      </section>
    );
  }

  const send = async () => {
    if (running.current) return;
    running.current = true;
    setSending(state.ready);
    setBusy(true);
    setProblem('');
    try {
      const result = await window.capturePointDesktop!.runExport(jobId);
      if (result.exported > 0) setDone(result);
      else onNotice(exportRunNotice(result), exportRunNoticeCode(result));
      const batch = await window.capturePointDesktop?.getLatestBatch(jobId);
      if (batch) onRefresh(batch);
    } catch (reason) {
      setProblem(reason instanceof Error ? reason.message : 'Export could not finish. Try again.');
    } finally {
      running.current = false;
      setBusy(false);
      counted.recount();
    }
  };
  return (
    <section className="export-ready" aria-label="Export">
      <span className="export-ready-mark" aria-hidden="true">
        <FolderOutput size={16} />
      </span>
      <strong>{ready(state.ready)}</strong>
      <button type="button" className="primary-button small" onClick={() => void send()}>
        Export
      </button>
      {/* A <div>: the message can carry a Technical details disclosure, which a <p> may not hold. */}
      {problem && (
        <div className="export-ready-problem" role="alert" data-telemetry="error:export-failed">
          <OperatorErrorText message={problem} />
        </div>
      )}
    </section>
  );
}

/**
 * In the status bar while a folder is still being read behind the documents on screen:
 * the whole folder, "Reading 198 more files · 42 of 240", never the wave in hand (owner,
 * 2026-09-27: "Waves of forty means nothing to the user"). It used to be a card above the
 * documents list; the status bar is where running work is told (owner, October 3, 2026).
 */
export function FolderImportStatus({ progress }: { progress: FolderImportProgress | null }) {
  if (!progress) return null;
  const status = folderImportStatus(progress);
  return (
    <span className="folder-import-status" role="status" aria-live="polite">
      <LoaderCircle className="spin" size={13} aria-hidden="true" />
      <span className="folder-import-note">{status.text}</span>
      {status.fraction !== null && (
        <span className="folder-import-meter" aria-hidden="true">
          <span style={{ width: `${Math.round(status.fraction * 100)}%` }} />
        </span>
      )}
      {status.count && <strong>{status.count}</strong>}
    </span>
  );
}

/** During setup, one quiet line per stage: what comes next, and one thing worth knowing. */
export function SetupNextCardView({ stage }: { stage: string | undefined }) {
  const card = setupNextCard(stage);
  if (!card) return null;
  return (
    <p className="setup-next" role="note" aria-live="polite">
      <span className="setup-next-label">Next</span>
      <span>
        <strong>{card.next}</strong>
        {card.tip && <small>{card.tip}</small>}
      </span>
    </p>
  );
}

/**
 * A job whose batch emptied stays in its workspace (owner, 2026-10-03). Exported documents
 * leave the batch, and an empty batch used to swap the whole workspace for the job's start
 * screen ("How would you like to start?"), so the moment a person finished, the screen they
 * had been working in disappeared. The documents list keeps its place and filters, and says
 * the list is clear; the main area says All caught up and, when the job exported in the
 * last 30 minutes, how many went and a big Open folder. Adding pages stays underneath.
 */
function useRecentExport(jobId: string): { count: number; exportId: string } | null {
  const [finished, setFinished] = useState<{ count: number; exportId: string } | null>(null);
  useEffect(() => {
    // PAIGE: the desktop's export record (`desktop-export`); Paige's destinations keep their own.
    if (!hostHas('desktop-export')) return;
    let current = true;
    const since = new Date(Date.now() - 30 * 60_000).toISOString();
    // Only documents that arrived: a failed, uncertain or still-delivering one was not exported.
    void window.capturePointDesktop
      ?.listExportedDocuments?.({ jobId, exportedFromUtc: since, state: 'exported' })
      .then((page) => {
        const exportId = page.documents[0]?.exportId;
        if (current && page.total > 0 && exportId) setFinished({ count: page.total, exportId });
      })
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [jobId]);
  return finished;
}

/** True when this job sends documents on its own as soon as they are ready. */
function useExportsByItself(jobId: string): boolean {
  const [automatic, setAutomatic] = useState(false);
  useEffect(() => {
    let live = true;
    // PAIGE: Paige sends as soon as ready to every destination besides Download (host-export.ts).
    if (!hostHas('desktop-export')) {
      void window.paigeWorkbench
        ?.exportFacts?.()
        .then((facts) => {
          if (live) setAutomatic((facts?.destinations ?? 0) > 0);
        })
        .catch(() => undefined);
      return () => {
        live = false;
      };
    }
    void window.capturePointDesktop
      ?.getExportSettings?.(jobId)
      .then((settings) => {
        if (live) setAutomatic(Boolean(settings?.enabled && settings.triggerMode === 'continuous'));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [jobId]);
  return automatic;
}

/** The documents list of a job with nothing in it right now, saying so. */
export function CaughtUpRail({ children }: { children?: React.ReactNode }) {
  return (
    <aside className="caught-up-rail" aria-label="Documents">
      {children && <div className="caught-up-rail-card">{children}</div>}
      <div className="spine-empty-state">
        <CheckCircle2 size={24} aria-hidden="true" />
        <p role="status">All caught up</p>
      </div>
    </aside>
  );
}

/**
 * The main area of a job with nothing in it right now. Finishing is the big moment, and Open
 * folder its biggest button. Reached by confirming the last waiting document, it says how many
 * were confirmed on this visit ("You confirmed 3"); the next step, exporting, is the export card
 * above the documents list.
 */
export function CaughtUpHero({
  jobId,
  onNotice,
  confirmed = 0,
}: {
  jobId: string;
  onNotice: PublishNotice;
  confirmed?: number;
}) {
  const finished = useRecentExport(jobId);
  const automatic = useExportsByItself(jobId);
  const exported = finished
    ? finished.count === 1
      ? '1 document exported'
      : `${finished.count.toLocaleString('en-US')} documents exported`
    : null;
  return (
    <div className="caught-up-hero" role="status">
      <span className="caught-up-mark" aria-hidden="true">
        <Check size={30} />
      </span>
      <h2>All caught up</h2>
      {confirmed > 0 && (
        <p className="caught-up-count caught-up-confirmed">
          You confirmed {confirmed.toLocaleString('en-US')}
        </p>
      )}
      {(exported || confirmed === 0) && (
        <p className="caught-up-count">{exported ?? 'Nothing is waiting for you'}</p>
      )}
      {finished && (
        <button
          type="button"
          className="primary-button caught-up-open"
          onClick={() =>
            void window.capturePointDesktop
              ?.openExportedDocument(finished.exportId, 'root')
              .catch(() =>
                onNotice('The export folder could not be opened.', 'export-folder-open-failed'),
              )
          }
        >
          <FolderOpen size={19} aria-hidden="true" /> Open folder
        </button>
      )}
      {automatic && (
        <p className="caught-up-auto">
          <Repeat size={15} aria-hidden="true" /> New ready documents export by themselves.
        </p>
      )}
    </div>
  );
}
